
import marked from "marked";
import Vue from "vue";
import axios from "axios";
import { upgradeImagesInElement, ImageUpgradeHandle } from "@/utils/progressiveImages";

let rendererMD = new marked.Renderer();
marked.setOptions({
  renderer: rendererMD,
  gfm: true,
  breaks: false,
  pedantic: false,
  sanitize: false,
  smartLists: true,
  smartypants: false,
});
rendererMD.heading = function (text, level) {
  // Some intro content embeds <audio> inside heading or adjacent HTML blocks; keep headings readable.
  const audioTags = (text.match(/<audio\b[\s\S]*?(?:<\/audio>|\/>)/gi) || []).map((t) =>
    /\bcontrols\b/i.test(t) ? t : t.replace(/<audio\b/i, "<audio controls")
  );
  const cleanText = text.replace(/<audio\b[\s\S]*?(?:<\/audio>|\/>)/gi, "").trim();
  return `<h${level} style="margin-bottom:20px;margin-top:20px">${cleanText}</h${level}>${audioTags.join("")}`;
};
rendererMD.link = function (href, title, text) {
  return `<a style="color:var(--color-primary);text-decoration:none;" href=${href}>${text}</a>`;
};

function normalizeAudioInHtml(html: string): string {
  // Rewrite <audio><source ...></audio> into a custom player to avoid unreliable native controls on some mobiles.
  const wrapStart = `<div class="aslp-audio-wrap">`;
  const wrapEnd = `</div>`;
  return html.replace(/<audio\b([\s\S]*?)>([\s\S]*?)<\/audio>/gi, (full, audioAttrs, inner) => {
    const audioSrcMatch = (audioAttrs || "").match(/\bsrc\s*=\s*(["'])(.*?)\1/i);
    const sourceSrcMatch = (inner || "").match(/<source\b[^>]*\bsrc\s*=\s*(["'])(.*?)\1/i);
    const src = (audioSrcMatch?.[2] || sourceSrcMatch?.[2] || "").trim();
    if (!src) return full;
    const safeSrc = src.replace(/"/g, "&quot;");
    const player = `
      <div class="aslp-audio-player" data-src="${safeSrc}">
        <button type="button" class="aslp-audio-player__btn" aria-label="播放/暂停">▶</button>
        <div class="aslp-audio-player__meta">
          <div class="aslp-audio-player__time">
            <span class="aslp-audio-player__cur">0:00</span>
            <span class="aslp-audio-player__sep">/</span>
            <span class="aslp-audio-player__dur">--:--</span>
          </div>
          <input class="aslp-audio-player__range" type="range" min="0" max="1000" value="0" step="1" />
        </div>
      </div>
    `.trim();
    return `${wrapStart}${player}${wrapEnd}`;
  });
}

export default Vue.extend({
  data: () => ({
    text: "",
    _imageUpgradeHandle: null as ImageUpgradeHandle | null,
  }),
  computed: {
    markdown(): string {
      return marked(this.text);
    },
  },
  mounted() {
    axios
      .post(Vue.prototype.apiUrl("get-item"), {
        collection: "intro",
        need: { index: -1 },
      })
      .then((response) => {
        let tmp = (response.data as any)[0].content;
        const html = Vue.prototype.replaceLocalhostInContent(tmp);
        this.text = normalizeAudioInHtml(html);
        this.$nextTick(() => {
          this.initAudioPlayers();
          const root = this.$refs.root as HTMLElement | undefined;
          if (root) this._imageUpgradeHandle = upgradeImagesInElement(root);
        });
      });
  },
  beforeDestroy() {
    this.destroyAudioPlayers();
    if (this._imageUpgradeHandle) {
      this._imageUpgradeHandle.destroy();
      this._imageUpgradeHandle = null;
    }
  },
  methods: {
    initAudioPlayers() {
      const root = this.$refs.root as HTMLElement | undefined;
      if (!root) return;
      this.destroyAudioPlayers();

      const players = Array.from(root.querySelectorAll<HTMLElement>(".aslp-audio-player"));
      const audios: HTMLAudioElement[] = [];

      const fmt = (sec: number) => {
        if (!isFinite(sec) || sec < 0) return "--:--";
        const s = Math.floor(sec);
        const m = Math.floor(s / 60);
        const r = s % 60;
        return `${m}:${String(r).padStart(2, "0")}`;
      };

      (this as any)._aslpPlayers = players.map((el) => {
        const src = el.getAttribute("data-src") || "";
        const audio = new Audio(src);
        audio.preload = "metadata";
        audios.push(audio);

        const btn = el.querySelector<HTMLButtonElement>(".aslp-audio-player__btn");
        const range = el.querySelector<HTMLInputElement>(".aslp-audio-player__range");
        const cur = el.querySelector<HTMLElement>(".aslp-audio-player__cur");
        const dur = el.querySelector<HTMLElement>(".aslp-audio-player__dur");

        const sync = () => {
          if (cur) cur.textContent = fmt(audio.currentTime || 0);
          if (dur) dur.textContent = fmt(audio.duration || NaN);
          if (range && isFinite(audio.duration) && audio.duration > 0) {
            range.value = String(Math.round((audio.currentTime / audio.duration) * 1000));
          }
          if (btn) btn.textContent = audio.paused ? "▶" : "❚❚";
        };

        const onBtnClick = async () => {
          try {
            if (audio.paused) {
              audios.forEach((a) => {
                if (a !== audio) a.pause();
              });
              await audio.play();
            } else {
              audio.pause();
            }
          } catch {
            // gesture restrictions; no-op
          }
          sync();
        };

        const onRangeInput = () => {
          if (!range) return;
          if (!isFinite(audio.duration) || audio.duration <= 0) return;
          const v = Number(range.value);
          audio.currentTime = (v / 1000) * audio.duration;
          sync();
        };

        const onTime = () => sync();
        const onMeta = () => sync();
        const onEnd = () => sync();

        btn?.addEventListener("click", onBtnClick);
        range?.addEventListener("input", onRangeInput);
        audio.addEventListener("timeupdate", onTime);
        audio.addEventListener("loadedmetadata", onMeta);
        audio.addEventListener("ended", onEnd);
        audio.addEventListener("pause", onTime);
        audio.addEventListener("play", onTime);

        sync();

        return {
          cleanup: () => {
            btn?.removeEventListener("click", onBtnClick);
            range?.removeEventListener("input", onRangeInput);
            audio.removeEventListener("timeupdate", onTime);
            audio.removeEventListener("loadedmetadata", onMeta);
            audio.removeEventListener("ended", onEnd);
            audio.removeEventListener("pause", onTime);
            audio.removeEventListener("play", onTime);
            audio.pause();
          },
        };
      });
    },
    destroyAudioPlayers() {
      const list = ((this as any)._aslpPlayers || []) as Array<{ cleanup: () => void }>;
      list.forEach((p) => {
        try {
          p.cleanup();
        } catch {
          // ignore
        }
      });
      (this as any)._aslpPlayers = [];
    },
  },
});
